/* Thẻ cuộc gọi đến của Tổng đài Sales: nhận diện khách theo số, quá thời gian đổ chuông thì thành cuộc nhỡ */
import React, { useEffect, useState } from 'react';
import { PhoneCall, PhoneIncoming, PhoneOff } from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import {
  maskPhone, nextStep, shortTag, staffOf, tagOf, textOn, when, codeLabel, type LeadLocal,
} from './logic';

/** Đổ chuông tối đa N giây rồi tính là nhỡ */
export const RING_SEC = 20;

export interface Ring { id: string; at: number; phone: string; r?: ChannelCustomerItem }

export default function IncomingCall({ ring, l, tags, now, onAnswer, onMiss }: {
  ring: Ring;
  l?: LeadLocal;
  tags: CustomerTagItem[];
  now: number;
  onAnswer: () => void;
  /** Từ chối hoặc hết thời gian đổ chuông */
  onMiss: (reason: 'decline' | 'timeout') => void;
}) {
  const [left, setLeft] = useState(RING_SEC);
  useEffect(() => {
    const id = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { if (left <= 0) onMiss('timeout'); }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  const { r } = ring;
  const tag = r ? tagOf(r, l, tags) : null;
  const step = r ? nextStep(r, l, now, tag?.name) : null;
  const staff = r ? staffOf(r, l) : null;
  const last = l?.touches[0];

  return (
    <div role="alertdialog" aria-label="Cuộc gọi đến" className="fixed right-4 top-20 z-[70] w-[340px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl bg-gray-900 text-white shadow-2xl ring-1 ring-white/10">
      <div className="flex items-center justify-between px-4 pt-3 text-[12px]">
        <span className="flex items-center gap-1.5 font-semibold text-emerald-300">
          <PhoneIncoming size={14} className="animate-pulse" /> Cuộc gọi đến
        </span>
        <span className="tabular-nums text-white/60">Đổ chuông {left}s</span>
      </div>
      <div className="px-4 pb-4 pt-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[16px] font-semibold">{r ? r.display_name || r.fullname || 'Khách' : 'Số lạ'}</div>
            <div className="text-[12px] tabular-nums text-white/60">
              {maskPhone(ring.phone)}{r ? ` · ${r.customer_code || `KH-${r.id}`}` : ' · chưa có hồ sơ'}
            </div>
          </div>
          {tag && <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: tag.color || '#64748b', color: textOn(tag.color) }}>{shortTag(tag.name)}</span>}
        </div>
        <div className="mt-2 space-y-0.5 rounded-lg bg-white/5 px-3 py-2 text-[12px] text-white/80">
          {r ? (
            <>
              <div>Telesales: <b className="text-white">{staff?.name ?? 'Chưa phân'}</b></div>
              {step && step.kind !== 'none' && <div>Việc đang chờ: <b className="text-white">{step.label}</b></div>}
              {last && <div>Chạm cuối: {when(last.at, now)}{last.code ? ` · ${last.code} ${codeLabel(last.code)}` : last.text ? ` · ${last.text}` : ''}</div>}
            </>
          ) : (
            <div>Số chưa có trong danh sách khách — nghe máy rồi tạo hồ sơ khách (R12).</div>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={() => onMiss('decline')} className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full bg-red-500 text-[13px] font-semibold hover:bg-red-600">
            <PhoneOff size={16} /> Từ chối
          </button>
          <button type="button" onClick={onAnswer} className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-500 text-[13px] font-semibold hover:bg-emerald-600">
            <PhoneCall size={16} /> Nghe
          </button>
        </div>
      </div>
    </div>
  );
}
